import { Component, Input, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { Observable, debounceTime, distinctUntilChanged, filter, tap } from 'rxjs';
import * as fromLanguages from '@app/root-store/dictionaries/languages';
import * as fromTemplateForm from '../../../store/template-form';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { Language, NewsContent, NewsForm, NewsImage } from '@app/types';
import { RdsAccordionDirective, RdsDialogService, RdsExpansionPanelComponent, RdsSelectFilterByFunc, RdsSelectOptionComponent } from '@rds/angular-components';
import { AuthService } from '@app/auth/auth.service';
import * as CKEDITOR from '@ckeditor/ckeditor5-build-classic';
import { NewsService } from '@app/news/news.service';
import { SubSink } from 'subsink';
import * as _ from 'lodash';
import { FormStepStatus } from '../../news-form/form-status-badge/form-status-badge.component';
import { TemplateForm } from '@app/types/template';

@Component({
  selector: 'rnb-template-form-content',
  templateUrl: './template-form-content.component.html',
  styleUrls: [ './template-form-content.component.scss' ]
})
export class TemplateFormContentComponent implements OnInit, OnDestroy {
  @ViewChildren(RdsExpansionPanelComponent) languagePanels: QueryList<RdsExpansionPanelComponent>
  private subs: SubSink = new SubSink;
  public Editor = CKEDITOR.NewsEditor;
  languageFocused = true;
  
  _isActive: Array<boolean>;
  get isActive(): Array<boolean> {
      return this._isActive;
  }
  @Input() set isActive(value: Array<boolean>) {
      this._isActive = value;
      this.store$.dispatch(fromTemplateForm.setContentFormStatus({status: this.form.status}));
      if (value) {
      this.store$.dispatch(fromTemplateForm.setContentStepStatus({status: FormStepStatus.IN_PROGRESS}));
      } else {
        this.form.markAllAsTouched();
        if (this.form.valid) {
          this.store$.dispatch(fromTemplateForm.setContentStepStatus({status: FormStepStatus.COMPLETED}));
        } else {
          this.store$.dispatch(fromTemplateForm.setContentStepStatus({status: FormStepStatus.INCOMPLETE}));
        }
      }
  }

  languages$: Observable<Array<Language>> = this.store$.pipe(select(fromLanguages.selectAll));
  now = new Date();

  
  filterBy: RdsSelectFilterByFunc = (
    text: string | null,
    item: RdsSelectOptionComponent
  ) => {
    if (text === null || text === "") {
      return true;
    } else {
      return item.value.name.toLowerCase().includes(text.toLowerCase());
    }
  };
  allLanguages$: Observable<Array<string>> = this.store$.pipe(select(fromTemplateForm.selectLanguages));

  primaryLanguage$: Observable<string> = this.store$.pipe(select(fromTemplateForm.selectPrimaryLanguage));
  primaryLanguage: string;

  primaryLanguageImage$: Observable<NewsImage> = this.store$.pipe(select(fromTemplateForm.selectPrimaryLanguageImage));
  primaryImageForm: FormGroup = new FormGroup({
    image: new FormControl(null)
  });

  form$: Observable<Partial<TemplateForm>> = this.store$.pipe(select(fromTemplateForm.selectContentForm));
  public form: FormGroup = new FormGroup({
    content: new FormArray([]),
  });

  get contentArray() {
    return this.form.controls.content as FormArray
  }

  get primaryContentForm() {
    return this.contentArray.controls.filter((a: FormGroup) => a.controls.language.value === this.primaryLanguage);
  }

  get otherContentForms() {
    return this.contentArray.controls.filter((a: FormGroup) => a.controls.language.value !== this.primaryLanguage);
  }

  get getArrayControlsInOrder() {
    return [...this.primaryContentForm, ...this.otherContentForms];
  }

  constructor(private dialogService: RdsDialogService, private newsService: NewsService, private store$: Store<any>, private auth: AuthService) {
    
  }

  ngOnDestroy(): void {
    this.form.markAllAsTouched();
    this.subs.unsubscribe();
  }

  ngOnInit(): void {
    this.store$.dispatch(fromLanguages.loadLanguages());
  
    this.subs.sink = this.primaryLanguage$.subscribe(language => {
      this.primaryLanguage = language;
    });

    this.subs.sink = this.primaryLanguageImage$.pipe(
      distinctUntilChanged((prev, next) => JSON.stringify(prev) === JSON.stringify(next)),
    ).subscribe(image => {
      this.primaryImageForm.controls.image.setValue({ ...image});
    });

    this.subs.sink = this.primaryImageForm.controls.image.valueChanges.subscribe(image => {
      this.otherContentForms.filter(c => c.value.syncImage).map(c => (c as FormGroup).controls.image.patchValue(image));
    })

    this.subs.sink = this.form$.pipe(
      distinctUntilChanged(),
      debounceTime(300)
    ).subscribe(news => {
      const formContentArray = (this.form.controls.content as FormArray).value as Array<NewsContent>;
      const formContentKeys = formContentArray.map(c => c.language);
      const newsContentKeys = news.content.map(c => c.language);
      const primaryContent = news.content.find(c => c.language === news.language);
      const toAdd = news.content.filter(c => !formContentKeys.includes(c.language));
      const toRemove = formContentArray.filter(c => !newsContentKeys.includes(c.language));
      toAdd.forEach(content => {
        (this.form.controls.content as FormArray).push(new FormGroup({
          language: new FormControl(content.language, [Validators.required]),
          title: new FormControl(content.title, [Validators.required, Validators.maxLength(100)]),
          abstract: new FormControl(content.abstract, [Validators.required, Validators.maxLength(200)]),
          image: new FormControl(content.image),
          html: new FormControl(content.html, [Validators.required]),
          syncImage: new FormControl(content.syncImage)
        }), {emitEvent: false})
      });
      toRemove.forEach(content => {
        const formContentArray = (this.form.controls.content as FormArray).value as Array<NewsContent>;
        const id = formContentArray.findIndex(c => c.language === content.language);
        (this.form.controls.content as FormArray).removeAt(id, {emitEvent: false})
      });
      const newsClone: Partial<NewsForm> = _.cloneDeep(news);
      newsClone.content.map(c => {
        delete c.html
        return c
      });
      this.form.patchValue(newsClone, {emitEvent: false});
      this.store$.dispatch(fromTemplateForm.setContentFormStatus({status: this.form.status}));
    });

    this.subs.sink = this.form.valueChanges.pipe(
      distinctUntilChanged(),
      debounceTime(50)
    ).subscribe(form => {
      this.store$.dispatch(fromTemplateForm.setFormValue({form}))
    });

    this.subs.sink = this.form.statusChanges.subscribe(value => {
      this.store$.dispatch(fromTemplateForm.setContentFormStatus({status: value}));
    });
    let langDifference = 0;
    this.subs.sink = this.allLanguages$.pipe(
      debounceTime(300),
      distinctUntilChanged((prev, next) => {
        langDifference = prev.length - next.length
        return prev.length - next.length === 0;
      })
    ).subscribe(languages => {
      if (langDifference === -1) {
        this.languagePanels.get(languages.length - 1).open()
      }
    });
  }

  deleteLanguageClick(event: MouseEvent, language: string) {
    event.stopPropagation();
    this.store$.dispatch(fromTemplateForm.removeLanguageVersion({language}))
  }

  syncImageChanged(form: FormGroup) {
    if (form.controls.syncImage.value === false) {
      form.controls.image.patchValue(this.primaryImageForm.controls.image.value);
    } else {
      form.controls.image.patchValue(null);
    }
  }

  focusAccordeon() {
    this.languageFocused = true;
  }

  unfocusAccordeon() {
    this.languageFocused = false;
  }

  change(accordion: RdsAccordionDirective, panel: RdsExpansionPanelComponent) {
    if (panel.expanded === false && accordion._headers.filter(h => h.panel.expanded === true).length === 0) {
      panel.open();
    }
  }

  uploadStarted(id) {
    this.store$.dispatch(fromTemplateForm.imageUploadStarted({id}))
  }

  uploadFailed(id) {
    this.store$.dispatch(fromTemplateForm.imageUploadFailed({id}))
  }

  uploadEnded(id) {
    this.store$.dispatch(fromTemplateForm.imageUploadEnded({id}))
  }

  uploadAborted(id) {
    this.store$.dispatch(fromTemplateForm.imageUploadAborted({id}))
  }
}
